import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  Divider,
  IconButton,
  styled,
  alpha,
  Badge,
  CircularProgress,
  Tooltip,
  Zoom,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DocumentReuploadModal from '../modals/DocumentReuploadModal';
import { useSelector, useDispatch } from 'react-redux';
import { notifications, setNotiFications } from '../../Store/userSlice';
import { postWrapperWeb, getWrapperWeb } from '../../utils/Wrapper';
import { NOTIFICATION_ENDPOINTS } from '../../constants/apiEndpoints';
import { WEB_BASE_URL } from '../../services/Axios';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

const NotificationTitle = styled(Box)(({ theme }) => ({
  padding: '28px 24px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  backgroundColor: '#fff',
  borderBottom: '1px solid #E5E8EB',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backdropFilter: 'blur(8px)',
  backgroundColor: alpha('#fff', 0.97),
}));

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

const ActionRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: '4px',
});

const NotificationContainer = styled(Box)({
  width: '420px',
  height: '100%',
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '6px',
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0) 100%)',
  },
});

const NotificationItem = styled(ListItem)(({ unread, isRead }) => ({
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  backgroundColor: unread ? alpha('#0052CC', 0.03) : '#fff',
  position: 'relative',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  borderLeft: unread ? '3px solid #0052CC' : '3px solid transparent',
  '&:hover': {
    backgroundColor: unread ? alpha('#0052CC', 0.06) : alpha('#000', 0.01),
    transform: 'translateX(4px)',
  },
  opacity: isRead ? 0.8 : 1,
}));

const NotificationContent = styled(Typography)({
  fontSize: '14px',
  color: '#637381',
  marginTop: '12px',
  lineHeight: 1.6,
  letterSpacing: '0.2px',
});

const ActionButton = styled(Button)({
  backgroundColor: '#0052CC',
  color: '#fff',
  fontWeight: 600,
  padding: '10px 20px',
  borderRadius: '10px',
  textTransform: 'none',
  marginTop: '20px',
  boxShadow: '0 2px 8px rgba(0,82,204,0.25)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#003E99',
    boxShadow: '0 4px 12px rgba(0,82,204,0.35)',
    transform: 'translateY(-2px)',
  },
});

const MarkAllButton = styled(Button)({
  color: '#0052CC',
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'none',
  padding: '8px 16px',
  borderRadius: '10px',
  backgroundColor: alpha('#0052CC', 0.04),
  '&:hover': {
    backgroundColor: alpha('#0052CC', 0.08),
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease-in-out',
});

const TimeStamp = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginTop: '12px',
  color: '#919EAB',
  fontSize: '12px',
  fontWeight: 500,
  opacity: 0.9,
});

const EmptyState = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '80px 24px',
  textAlign: 'center',
  height: '100%',
  backgroundColor: alpha('#F4F6F8', 0.5),
  backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(145, 158, 171, 0.05) 0%, rgba(145, 158, 171, 0) 100%)',
});

const LoadingState = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '80px 24px',
  height: '100%',
  backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(145, 158, 171, 0.03) 0%, rgba(145, 158, 171, 0) 100%)',
});

const NotificationBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#FF4842',
    color: '#fff',
    minWidth: '22px',
    height: '22px',
    padding: '0 6px',
    fontSize: '12px',
    fontWeight: 600,
    boxShadow: '0 2px 6px rgba(255,72,66,0.25)',
  },
}));

const NotificationsPopup = ({ anchorEl, open, onClose }) => {
  const [reuploadModalOpen, setReuploadModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const previousNotification = useSelector(notifications);
  const user = useSelector(state => state.auth.user);
  const dispatch = useDispatch();
  
  const [readNotifications, setReadNotifications] = useState(() => 
    new Set(JSON.parse(localStorage.getItem('readNotifications') || '[]'))
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef(null);

  const PAGE_SIZE = 10;

  const fetchNotifications = useCallback(
    async (page = 0, append = false) => {
      const isAppend = Boolean(append);
      try {
        isAppend ? setIsLoadingMore(true) : setIsLoading(true);
        const role = user?.role === 'SUPERADMIN' ? 'ADMIN' : 'MANAGER';
        const response = await getWrapperWeb(
          `${NOTIFICATION_ENDPOINTS.GET_NOTIFICATIONS}?role=${role}&page=${page}&size=${PAGE_SIZE}`
        );

        const apiResult = Array.isArray(response?.result?.content)
          ? response.result.content
          : Array.isArray(response?.result)
            ? response.result
            : [];

        if (response?.statusCode !== 200) return;

        const formattedNotifications = apiResult
          .map(notification => ({
            ...notification,
            id: notification.id,
          }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        if (isAppend) {
          const existingNotifications = previousNotification || [];
          const combinedNotifications = [...existingNotifications, ...formattedNotifications];
          const uniqueNotifications = combinedNotifications.filter(
            (notification, index, self) =>
              index === self.findIndex(n => n.id === notification.id)
          );
          dispatch(setNotiFications(uniqueNotifications));
        } else {
          dispatch(setNotiFications(formattedNotifications));
        }

        const isLastPage = response?.result?.last ?? (apiResult.length < PAGE_SIZE);

        setHasMore(!isLastPage);
        setCurrentPage(page);
      } catch (error) {
        toast.error('Failed to fetch notifications');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [dispatch, user, previousNotification]
  );

  const loadMoreNotifications = useCallback(
    () => {
      if (!hasMore || isLoadingMore || isLoading) return;

      const nextPage = currentPage + 1;
      return fetchNotifications(nextPage, true);
    },
    [hasMore, isLoadingMore, isLoading, currentPage, fetchNotifications]
  );

  useEffect(() => {
    if (open) {
      setCurrentPage(0);
      setHasMore(true);
      fetchNotifications(0, false);
    }
  }, [open]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const threshold = 100;
      
      if (scrollHeight - scrollTop - clientHeight < threshold && hasMore && !isLoadingMore && !isLoading) {
        loadMoreNotifications();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [hasMore, isLoadingMore, isLoading, loadMoreNotifications]);

  const markNotificationsAsRead = async (notificationId = null) => {
    try {
      const role = user?.role === 'SUPERADMIN' ? 'ADMIN' : 'MANAGER';
      const response = await postWrapperWeb(`${NOTIFICATION_ENDPOINTS.MARK_READ}?role=${role}${notificationId ? `&notificationId=${notificationId}` : ""}`);

      if (response?.statusCode === 200 && previousNotification) {
        setCurrentPage(0);
        setHasMore(true);
        await fetchNotifications(0, false);

        if (notificationId) {
          setReadNotifications(new Set([...readNotifications, notificationId]));
          // toast.success('Notification marked as read');
        } else {
          const allIds = previousNotification.map(n => n.id);
        setReadNotifications(new Set([...readNotifications, ...allIds]));
          // toast.success('All notifications marked as read');
        }
      }
    } catch (error) {
      // toast.error('Failed to mark notifications as read');
    }
  };

  useEffect(() => {
    localStorage.setItem('readNotifications', JSON.stringify([...readNotifications]));
  }, [readNotifications]);

  const handleActionClick = async (actionUrl, notificationId, action) => {
    if (notificationId) {      
      try {
        await markNotificationsAsRead(notificationId);
        
        if (actionUrl === '/application-history') {
          setReuploadModalOpen(true);
        } else if (actionUrl) {
          const normalizedUrl = actionUrl === '/loans' ? '/proposals' : actionUrl;
          window.location.href = normalizedUrl;
        }
      } catch (error) {
        toast.error('Error handling action click:', error);
      }
    }
    onClose();
  };

  const handleNotificationClick = async (notificationId, hasAction) => {    
    if (!hasAction && notificationId) {
      try {
        await markNotificationsAsRead(notificationId);
      } catch (error) {
        toast.error('Failed to mark notification as read');
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return format(new Date(timestamp), 'MMM d, yyyy • h:mm a');
    } catch (error) {
      return timestamp;
    }
  };

  const unreadCount = previousNotification?.filter(n => n.unread && !readNotifications.has(n.id)).length || 0;

  const renderContent = () => {
    if (isLoading) {
      return (
        <Fade in={true} timeout={300}>
          <LoadingState>
            <CircularProgress 
              size={44} 
              sx={{ 
                color: '#0052CC',
                mb: 3,
                opacity: 0.8,
              }} 
            />
            <Typography sx={{ 
              color: '#637381',
              fontWeight: 500,
              fontSize: '15px',
            }}>
              Loading notifications...
            </Typography>
          </LoadingState>
        </Fade>
      );
    }

    if (!previousNotification || previousNotification.length === 0) {
      return (
        <Fade in={true} timeout={300}>
          <EmptyState>
            <NotificationsNoneIcon 
              sx={{ 
                fontSize: 64,
                color: '#919EAB',
                mb: 3,
                opacity: 0.8,
              }} 
            />
            <Typography 
              sx={{ 
                fontSize: '20px',
                fontWeight: 600,
                color: '#212B36',
                mb: 1.5,
              }}
            >
              No notifications yet
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '15px',
                color: '#637381',
                maxWidth: '300px',
                lineHeight: 1.6,
              }}
            >
              We'll notify you when something important happens in your account
            </Typography>
          </EmptyState>
        </Fade>
      );
    }

    return (
      <Box
        ref={scrollContainerRef}
        sx={{ 
          overflowY: 'auto',
          height: 'calc(100% - 90px)',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha('#919EAB', 0.35),
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: alpha('#919EAB', 0.45),
            },
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: alpha('#919EAB', 0.08),
            borderRadius: '4px',
          },
        }}
      >
      <List disablePadding>
        {previousNotification.map((notification, index) => {
          const notificationId = notification.id || `notification-${Date.now()}-${index}`;
          const isRead = readNotifications.has(notificationId);
          const showUnread = notification.unread && !isRead;
          const hasAction = !!notification.action;

          return (
            <Box key={notificationId} sx={{ position: 'relative' }}>
              <NotificationItem 
                unread={showUnread}
                isRead={isRead}
                onClick={() => handleNotificationClick(notificationId, hasAction)}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  width: '100%',
                  gap: 1.5,
                }}>
                  <Typography 
                    sx={{
                      fontWeight: 600,
                      fontSize: '15px',
                      color: '#212B36',
                      flex: 1,
                      lineHeight: 1.5,
                      letterSpacing: '-0.2px',
                    }}
                  >
                    {notification.title}
                  </Typography>
                  {showUnread ? (
                    <Tooltip title="Unread notification" arrow>
                      <FiberManualRecordIcon 
                        sx={{ 
                          fontSize: 10, 
                          color: '#0052CC',
                          mt: 0.8,
                          filter: 'drop-shadow(0 2px 4px rgba(0,82,204,0.2))',
                        }} 
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Read notification" arrow>
                      <CheckCircleOutlineIcon 
                        sx={{ 
                          fontSize: 16, 
                          color: '#919EAB',
                          mt: 0.5,
                          opacity: 0.8,
                        }} 
                      />
                    </Tooltip>
                  )}
                </Box>
                
                <NotificationContent>
                  {notification.content}
                </NotificationContent>

                <TimeStamp>
                  <AccessTimeIcon sx={{ fontSize: 14 }} />
                  {formatTimestamp(notification.createdAt)}
                </TimeStamp>
                
                {notification.action && (
                  <ActionButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleActionClick(notification.actionUrl, notificationId, notification.action);
                    }}
                  >
                    {notification.action}
                  </ActionButton>
                )}
              </NotificationItem>
              
              {index < previousNotification.length - 1 && (
                <Divider sx={{ margin: '0 24px', opacity: 0.6 }} />
              )}
            </Box>
          );
        })}
        {isLoadingMore && (
          <Box sx={{ padding: '20px', textAlign: 'center' }}>
            <CircularProgress 
              size={24} 
              sx={{ 
                color: '#0052CC',
                mb: 1,
              }} 
            />
            <Typography sx={{ color: '#637381', fontSize: '14px' }}>
              Loading more notifications...
            </Typography>
          </Box>
        )}
      </List>
      </Box>
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 420,
            maxWidth: '100%',
            boxShadow: '-12px 0 32px rgba(0,0,0,0.12)',
            backgroundImage: 'linear-gradient(to right, rgba(145, 158, 171, 0.02), rgba(145, 158, 171, 0))',
          }
        }}
        SlideProps={{
          timeout: 300
        }}
      >
        <NotificationContainer>
          <NotificationTitle>
            <HeaderRow>
              <Typography 
                sx={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#212B36',
                  display: 'flex',
                  alignItems: 'center',
                  letterSpacing: '-0.5px',
                }}
              >
                Notifications
                {unreadCount > 0 && (
                  <NotificationBadge
                    badgeContent={unreadCount}
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>
              <IconButton 
                onClick={onClose} 
                size="small"
                sx={{ 
                  color: '#637381',
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: alpha('#637381', 0.08),
                    transform: 'rotate(90deg)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                <CloseIcon />
              </IconButton>
            </HeaderRow>
            {unreadCount > 0 && (
              <ActionRow>
                <MarkAllButton
                  onClick={() => markNotificationsAsRead()}
                  startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
                >
                  Mark all as read
                </MarkAllButton>
              </ActionRow>
            )}
          </NotificationTitle>
          
          {renderContent()}
        </NotificationContainer>
      </Drawer>

      <DocumentReuploadModal 
        open={reuploadModalOpen}
        onClose={() => setReuploadModalOpen(false)}
      />
    </>
  );
};

export default NotificationsPopup; 